import React, { useState, useEffect } from 'react';
import axios from '../config/axiosConfig';
import { MdPhotoLibrary, MdClose, MdArrowBack, MdArrowForward } from 'react-icons/md';

function Photos() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(null);

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const response = await axios.get('nai-gallery/');
      setGalleries(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching photos:", error);
      setLoading(false);
    }
  };

  const openLightbox = (index) => {
    setLightboxImageIndex(index);
  };

  const closeLightbox = () => {
    setLightboxImageIndex(null);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (selectedGallery && lightboxImageIndex !== null) {
      setLightboxImageIndex((prev) => (prev + 1) % selectedGallery.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (selectedGallery && lightboxImageIndex !== null) {
      setLightboxImageIndex((prev) => (prev - 1 + selectedGallery.images.length) % selectedGallery.images.length);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Photo Gallery
          </h1>
          <p className="mt-4 text-xl text-gray-500">
            Glimpses of our events and activities.
          </p>
        </div>

        {loading ? (
             <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
             </div>
        ) : selectedGallery ? (
            /* Gallery Detail View */
            <div>
                 <button 
                    onClick={() => setSelectedGallery(null)}
                    className="mb-6 flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors"
                 >
                     <MdArrowBack className="mr-2" /> Back to Albums
                 </button>
                 
                 <div className="mb-8">
                     <h2 className="text-2xl font-bold text-gray-900">{selectedGallery.title}</h2>
                     {selectedGallery.description && (
                         <p className="mt-2 text-gray-600">{selectedGallery.description}</p>
                     )}
                     <p className="mt-1 text-sm text-gray-400">{new Date(selectedGallery.created_at).toLocaleDateString()}</p>
                 </div>

                 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                     {selectedGallery.images.map((img, index) => (
                         <div 
                            key={img.id} 
                            onClick={() => openLightbox(index)}
                            className="cursor-pointer relative group rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 aspect-w-1 aspect-h-1"
                         >
                             <img 
                                src={img.image} 
                                alt={`Gallery image ${index + 1}`} 
                                className="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-500"
                             />
                             <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300"></div>
                         </div>
                     ))}
                 </div>
            </div>
        ) : (
            /* Albums List View */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {galleries.map((gallery) => (
                <div 
                    key={gallery.id} 
                    onClick={() => setSelectedGallery(gallery)}
                    className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 block h-full flex flex-col"
                >
                    <div className="relative h-64 bg-gray-200">
                        {gallery.images && gallery.images.length > 0 ? (
                            <img 
                                src={gallery.images[0].image} 
                                alt={gallery.title} 
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-400">
                                <MdPhotoLibrary className="w-16 h-16 opacity-50" />
                            </div>
                        )}
                        <div className="absolute bottom-0 right-0 bg-black/70 text-white text-xs font-bold px-3 py-1 rounded-tl-lg">
                             {gallery.images ? gallery.images.length : 0} Photos
                        </div>
                    </div>
                    
                    <div className="p-6 flex-grow flex flex-col justify-between">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{gallery.title}</h3>
                            {gallery.description && (
                                <p className="text-gray-600 text-sm line-clamp-2">{gallery.description}</p>
                            )}
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                            <span className="text-xs text-gray-500 font-medium">
                                {new Date(gallery.created_at).toLocaleDateString()}
                            </span>
                            <span className="text-blue-600 text-sm font-semibold hover:text-blue-800">
                                View Album
                            </span>
                        </div>
                    </div>
                </div>
              ))}
              
              {galleries.length === 0 && (
                  <div className="col-span-full text-center py-12">
                      <MdPhotoLibrary className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-lg text-gray-500">No photo albums available yet.</p>
                  </div>
              )}
            </div>
        )}

        {/* Simple Lightbox */}
        {selectedGallery && lightboxImageIndex !== null && (
            <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={closeLightbox}>
                 <button 
                    onClick={closeLightbox}
                    className="absolute top-4 right-4 text-white hover:text-gray-300 z-50 p-2"
                 >
                     <MdClose size={32} />
                 </button>
                 
                 <button 
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2 z-50 bg-black/50 rounded-full"
                 >
                     <MdArrowBack size={24} />
                 </button>

                 <img 
                    src={selectedGallery.images[lightboxImageIndex].image} 
                    alt="Full screen" 
                    className="max-h-[90vh] max-w-[90vw] object-contain shadow-2xl"
                    onClick={(e) => e.stopPropagation()} // Prevent closing when clicking image
                 />

                 <button 
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2 z-50 bg-black/50 rounded-full"
                 >
                     <MdArrowForward size={24} />
                 </button>
                 
                 <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
                     {lightboxImageIndex + 1} / {selectedGallery.images.length}
                 </div>
            </div>
        )}

      </div>
    </div>
  );
}

export default Photos;
